import {
  Component,
  OnInit,
  HostListener,
  Input,
  EventEmitter,
  Output,
} from '@angular/core';
import { MenuService } from '../../../core/utilities/menu.service';
import { TranslateService } from '@ngx-translate/core';
import { BaseComponent } from '../../base/BaseComponent';
import {
  MenuNode,
  MenuData,
  LangCode,
  ViewAllMeta,
  SearchSuggestion,
} from 'src/app/core/models/menu/menu.models';
import { LanguageService } from 'src/app/services/language.service';

declare const UserWay: any;

@Component({
  selector: 'app-header-menu-new',
  templateUrl: './header-menu-new.component.html',
  styleUrls: ['./header-menu-new.component.scss'],
})
export class HeaderMenuNewComponent extends BaseComponent implements OnInit {
  mainWebsiteLink: string = 'https://www.moccae.gov.ae';

  menus: MenuNode[] = [];
  utilities: MenuNode[] = [];

  @Input() mobileOpen = false;
  @Output() mobileOpenChange = new EventEmitter();
  private mobileOpenId: string | null = null;
  langDropdownOpen = false;
  accessibilityDropdownOpen = false;
  accessibilityMobileOpen = false;

  mobileSearchOpen = false;
  searchQuery = '';

  private allSuggestions: Array<SearchSuggestion> = [
    // Seed examples — swap with real endpoints later if needed
    {
      id: 'establishments',
      label: { ar: 'المنشآت المسجلة', en: 'Registered Establishments' },
      href: { ar: '/ar/our-services', en: '/en/our-services' },
    },
    {
      id: 'climate-change',
      label: { ar: 'التغير المناخي', en: 'Climate Change' },
      href: {
        ar: 'https://moccae.gov.ae/ar/knowledge/climate-change',
        en: 'https://moccae.gov.ae/en/knowledge/climate-change',
      },
    },
    {
      id: 'legislations',
      label: { ar: 'التشريعات', en: 'Legislations' },
      href: {
        ar: 'https://moccae.gov.ae/ar/legislations',
        en: 'https://moccae.gov.ae/en/legislations',
      },
    },
    {
      id: 'news-media',
      label: { ar: 'الأخبار والإعلام', en: 'News & Media' },
      href: {
        ar: 'https://moccae.gov.ae/ar/media-center/news-and-media',
        en: 'https://moccae.gov.ae/en/media-center/news-and-media',
      },
    },
    {
      id: 'export-import',
      label: { ar: 'التصدير والاستيراد', en: 'Export & Import' },
      href: {
        ar: '/ar/services?cat=export-import',
        en: '/en/services?cat=export-import',
      },
    },
    {
      id: 'municipal-affairs',
      label: { ar: 'الشؤون البلدية', en: 'Municipal Affairs' },
      href: {
        ar: 'https://moccae.gov.ae/ar/knowledge/municipal-affairs',
        en: 'https://moccae.gov.ae/en/knowledge/municipal-affairs',
      },
    },
  ];

  get filteredSuggestions() {
    const q = (this.searchQuery || '').trim().toLowerCase();
    if (!q) return [];
    return this.allSuggestions
      .filter((s) => (s.label[this.lang] || '').toLowerCase().includes(q))
      .slice(0, 8);
  }

  constructor(
    translateService: TranslateService,
    private languageService: LanguageService,
    private menuService: MenuService
  ) {
    super(translateService);
  }

  ngOnInit(): void {
    this.menuService.load().subscribe((data: MenuData) => {
      this.mainWebsiteLink = data.mainWebsiteLink || this.mainWebsiteLink;
      this.menus = data.menus || [];
      this.utilities = data.utilities || [];
    });
  }

  // ------- Helpers
  labelOf(node: { label: { [k in LangCode]?: string } }): string {
    return node.label?.[this.lang] || node.label?.ar || node.label?.en || '';
  }
  hrefOf(node?: { href?: { [k in LangCode]?: string } }): string | null {
    if (!node?.href) return null;
    return (
      (node?.href?.en?.includes('https') ? '' : this.mainWebsiteLink) +
        node.href[this.lang] ||
      node.href.ar ||
      node.href.en ||
      null
    );
  }

  isMegaMenu = (n: MenuNode): boolean =>
    n?.id === 'services' && n.kind === 'menu';

  /** Groups = all direct children except the special quick-links group */
  groupsOf = (services: MenuNode): MenuNode[] =>
    (services?.children ?? []).filter(
      (c) => c.kind === 'group' && c.id !== 'grp-quick-links'
    );

  /** Quick links = children of the special quick-links group id */
  quickLinksOf = (services: MenuNode): MenuNode[] => {
    const q = (services?.children ?? []).find(
      (c) => c.id === 'grp-quick-links' && c.kind === 'group'
    );
    return q?.children ?? [];
  };

  viewAllMeta = (g: MenuNode): ViewAllMeta | undefined => g?.meta?.viewAll;

  trackById = (_: number, n: MenuNode) => n.id;

  trackByI = (_: number, n: SearchSuggestion) => n.id;

  // ------- Mobile
  toggleMobile(): void {
    this.mobileOpen = !this.mobileOpen;
    this.mobileOpenChange.emit(this.mobileOpen);
    if (!this.mobileOpen) {
      this.mobileOpenId = null;
      // close search when drawer is closed
      this.mobileSearchOpen = false;
      this.searchQuery = '';
    }
  }

  toggleMobileSection(id: string): void {
    this.mobileOpenId = this.mobileOpenId === id ? null : id;
  }

  isMobileOpen(id: string): boolean {
    return this.mobileOpenId === id;
  }

  toggleMobileSearch(): void {
    this.mobileSearchOpen = !this.mobileSearchOpen;
    if (!this.mobileSearchOpen) this.searchQuery = '';
    // Small UX touch: focus the input next tick
    setTimeout(() => {
      const el = document.querySelector<HTMLInputElement>('#mobileSearchInput');
      el?.focus();
    }, 0);
  }

  onSearchInput(ev: Event): void {
    const v = (ev.target as HTMLInputElement).value || '';
    this.searchQuery = v;
  }

  clearSearch(): void {
    this.searchQuery = '';
  }

  // ------- Accessibility
  toggleAccessibilityDropdown(): void {
    this.accessibilityDropdownOpen = !this.accessibilityDropdownOpen;
    if (this.accessibilityDropdownOpen) this.langDropdownOpen = false;
  }
  toggleAccessibilityMobile(): void {
    this.accessibilityMobileOpen = !this.accessibilityMobileOpen;
  }

  openAccessiblity(): void {
    let userway: HTMLElement = document?.getElementById(
      'userwayAccessibilityIcon'
    ) as HTMLElement;
    userway.click();
  }

  // ------- Language
  toggleLangDropdown(): void {
    this.langDropdownOpen = !this.langDropdownOpen;
    if (this.langDropdownOpen) this.accessibilityDropdownOpen = false;
  }
  // ...inside HeaderMenuNewComponent
  toggleLangMobile(): void {
    const next = this.lang === 'ar' ? 'en' : 'ar';
    this.changeLang(next);
    if (typeof UserWay !== 'undefined' && UserWay.changeWidgetLanguage) {
      UserWay.changeWidgetLanguage(next);
    }
  }

  changeLang(lang: string) {
    this.changeLanguage(lang);
    if (typeof UserWay !== 'undefined' && UserWay.changeWidgetLanguage) {
      UserWay.changeWidgetLanguage(lang);
    }
  }

  @HostListener('window:resize')
  onResize() {
    if (window.innerWidth > 900 && this.mobileOpen) {
      this.mobileOpen = false;
      this.mobileSearchOpen = false;
      this.searchQuery = '';
    }
  }

  @HostListener('document:click', ['$event'])
  onDocClick(e: Event) {
    const el = e.target as HTMLElement;
    if (
      el.closest('.utility.language') ||
      el.closest('.utility.accessibility') ||
      el.closest('.language-mobile') ||
      el.closest('.accessibility-mobile')
    )
      return;
    this.langDropdownOpen = false;
    this.accessibilityDropdownOpen = false;
  }
}
